import { useState, useEffect } from "react";
import { motion } from "motion/react";
import { Download, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import QRCode from "qrcode";

interface QRCodeGeneratorProps {
  url: string;
  size?: number;
  className?: string;
}

export function QRCodeGenerator({
  url,
  size = 200,
  className = "",
}: QRCodeGeneratorProps) {
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const generateQR = async () => {
      try {
        const dataUrl = await QRCode.toDataURL(url, {
          width: size,
          margin: 2,
          color: {
            dark: "#9B87F5",
            light: "#0a0a0a",
          },
          errorCorrectionLevel: "H",
        });
        setQrDataUrl(dataUrl);
      } catch (error) {
        console.error("Failed to generate QR code:", error);
      }
    };

    if (url) {
      void generateQR();
    }
  }, [url, size]);

  const handleDownload = () => {
    if (!qrDataUrl) return;

    const link = document.createElement("a");
    link.download = `qr-${url.split("/").pop()}.png`;
    link.href = qrDataUrl;
    link.click();
    toast.success("QR code downloaded!");
  };

  const handleCopy = async () => {
    if (!qrDataUrl) return;

    try {
      const response = await fetch(qrDataUrl);
      const blob = await response.blob();
      await navigator.clipboard.write([
        new ClipboardItem({ "image/png": blob }),
      ]);
      setCopied(true);
      toast.success("QR code copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy QR code");
    }
  };

  if (!qrDataUrl) {
    return (
      <div className={`flex items-center justify-center ${className}`}>
        <div className="w-[200px] h-[200px] bg-[#1A1F2C] rounded-lg animate-pulse" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`flex flex-col items-center gap-4 ${className}`}
    >
      <div className="relative p-4 bg-[#0a0a0a] rounded-lg border border-[#9B87F5]/20">
        <img src={qrDataUrl} alt="QR Code" className="rounded" />

        {/* Corner decorations */}
        <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-[#9B87F5]" />
        <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[#9B87F5]" />
        <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[#9B87F5]" />
        <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-[#9B87F5]" />
      </div>

      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleDownload}
          className="border-[#9B87F5]/30 text-[#9B87F5] hover:bg-[#9B87F5]/10 font-mono text-xs"
        >
          <Download className="w-3 h-3 mr-1" />
          DOWNLOAD
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleCopy}
          className={`border-[#9B87F5]/30 font-mono text-xs ${
            copied ? "text-green-400 border-green-400/30" : "text-[#9B87F5]"
          } hover:bg-[#9B87F5]/10`}
        >
          {copied ? (
            <>
              <Check className="w-3 h-3 mr-1" />
              COPIED
            </>
          ) : (
            <>
              <Copy className="w-3 h-3 mr-1" />
              COPY
            </>
          )}
        </Button>
      </div>
    </motion.div>
  );
}
